//@flow
import React, {useEffect, useRef, forwardRef, useImperativeHandle} from 'react';
import {
  IconButton,
  Slider,
  Typography,
  makeStyles,
  withStyles,
} from '@material-ui/core';
import {Theme} from '@material-ui/core';
import {Pause, PlayArrow} from '@material-ui/icons';
import {useState} from 'react';
import {colors} from '../../assets/styles/colors';
import {classNames, useCreateObjectUrl} from '../../utils/react';

type PropClasses = {
  root?: string,
};

type AudioPlayerProps = {
  src: string | Blob | File,
  disabled?: boolean,
  classes?: PropClasses,

  onPlay?: () => void,
  onPause?: () => void,
  onEnded?: () => void,
  onError?: () => void,
  onLoadedMetadata?: () => void,

  manualDurationForIndeterminateSource?: number,
};

const useStyles = makeStyles((theme: Theme) => ({
  root: {
    width: '100%',
    padding: '.5rem .75rem',
    borderRadius: '5rem',
    display: 'flex',
    alignItems: 'center',
    gap: '1rem',
  },
  duration: {
    fontSize: '.75rem',
    fontWeight: 600,
    color: colors.grey700,
  },
  progressIndicator: {
    position: 'absolute',
    top: 0,
    left: 0,
    height: '100%',
    width: '40%',
    background: theme.palette.primary.main,
  },
  progressTrack: {
    height: '100%',
    aspectRatio: 1,
    borderRadius: '50%',
    background: theme.palette.grey[500],
  },
}));

const CustomSlider = withStyles({
  root: {
    color: colors.grey300,
    height: 6,
    padding: '.75rem 0',
  },
  thumb: {
    height: 20,
    width: 20,
    backgroundColor: colors.primary,
    boxShadow: 'rgba(17, 12, 46, 0.1) 0px 0px 32px 0px',
    marginTop: -7,
    marginLeft: -12,
    '&:focus, &:hover, &$active': {
      boxShadow:
        '0 3px 1px rgba(0,0,0,0.1),0 4px 8px rgba(0,0,0,0.3),0 0 0 1px rgba(0,0,0,0.02)',
      // Reset on touch devices, it doesn't add specificity
      '@media (hover: none)': {
        boxShadow: 'rgba(17, 12, 46, 0.1) 0px 0px 32px 0px',
      },
    },
    '&:before': {
      display: 'none',
    },
  },
  active: {},

  track: {
    background: colors.primary,
    height: 6,
    borderRadius: 4,
  },
  rail: {
    height: 6,
    borderRadius: 4,
  },

  mark: {
    backgroundColor: 'currentColor',
    height: 6,
    width: 1,
    marginTop: -3,
  },
  markActive: {
    opacity: 1,
    backgroundColor: 'currentColor',
  },
})(Slider);

const calculateTime = (secs: number) => {
  //Check if secs is infinity for audio streams
  if (secs === Infinity) return '';
  const minutes = Math.floor(secs / 60);
  const seconds = Math.floor(secs % 60);
  const returnedSeconds = seconds < 10 ? `0${seconds}` : `${seconds}`;
  return `${minutes}:${returnedSeconds}`;
};

const AudioPlayer = forwardRef<AudioPlayerProps, any>(function AudioPlayer(
  props: AudioPlayerProps,
  ref
) {
  const {
    src,
    disabled,
    classes: propsClasses,
    manualDurationForIndeterminateSource,
    ...events
  } = props;
  const classes = useStyles();

  const {objectURL} = useCreateObjectUrl(typeof src !== 'string' ? src : null);

  const source = objectURL || src;

  const rootClasses = classNames(
    classes.root,
    propsClasses && propsClasses.root
  );

  const [playing, setPlaying] = useState(false);

  const [duration, setDuration] = useState('0:00');
  const [progress, setProgress] = useState(0);

  const audioRef = useRef<HTMLAudioElement | null>(null);

  //Forwarding ref to parent
  useImperativeHandle(ref, () => audioRef.current);

  useEffect(() => {
    const audio = audioRef.current;
    if (!audio) return;

    const handleTimeUpdate = () => {
      const currentTime = calculateTime(audio.currentTime);

      setProgress((audio.currentTime / audio.duration) * 100);

      setDuration(currentTime);
    };

    const handleEnded = () => {
      setPlaying(false);
    };

    const handleLoadedMetadata = () => {
      if (manualDurationForIndeterminateSource) {
        const duration = calculateTime(manualDurationForIndeterminateSource);
        setDuration(duration);
        return;
      }
      const duration = calculateTime(audio.duration);

      setProgress(0);

      setDuration(duration);
    };

    audio.addEventListener('timeupdate', handleTimeUpdate);
    audio.addEventListener('loadedmetadata', handleLoadedMetadata);
    audio.addEventListener('ended', handleEnded);

    return () => {
      audio.removeEventListener('timeupdate', handleTimeUpdate);
      audio.removeEventListener('loadedmetadata', handleLoadedMetadata);
      audio.removeEventListener('ended', handleEnded);
    };
  }, [manualDurationForIndeterminateSource, src]);

  const handleTogglePlaying = () => {
    const audio = audioRef.current;
    if (!audio) return;

    if (playing) {
      audio.pause();
    } else {
      audio.play();
    }

    setPlaying(!playing);
  };

  return (
    <div className={rootClasses}>
      <IconButton
        size="small"
        onClick={handleTogglePlaying}
        disabled={disabled}
      >
        {playing ? <Pause /> : <PlayArrow />}
      </IconButton>

      <audio {...events} src={source} ref={audioRef}></audio>

      <CustomSlider
        value={progress}
        disabled={disabled}
        onChange={(e, value) => {
          const audio = audioRef.current;
          if (!audio) return;

          //Check if secs is infinity for audio streams
          if (audio.duration === Infinity) return;

          audio.currentTime = (audio.duration / 100) * value;
        }}
        aria-labelledby="continuous-slider"
      />

      <Typography className={classes.duration}>{duration}</Typography>
    </div>
  );
});

export default AudioPlayer;
